import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Typewriter from "typewriter-effect";
import { introdata, meta } from "../../content_option";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const flicker = keyframes`
  0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
    opacity: 1;
  }
  20%, 22%, 24%, 55% {
    opacity: 0.8;
  }
`;


const HomeSection = styled.section`
  flex: 1 0 auto;
  position: relative;
  width: 100%;
  transition: all 0.5s ease-in;
  height: 100%;

  @media (max-width: 991.98px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100dvh;
    max-height: 100dvh;
    overflow: hidden;
    box-sizing: border-box;
    z-index: 1;
  }
`;

const IntroSection = styled.div`
  height: 100vh;
  min-height: 700px;
  margin-top: -60px;
  margin-left: 50px;

  @media (max-width: 991.98px) {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    min-height: unset;
    max-height: 100dvh;
    margin-left: 0;
    margin-top: 0;
    width: 100%;
    padding: 0;
    overflow: hidden;
    box-sizing: border-box;
  }
`;

const PhotoContainer = styled.div`
  @media (max-width: 991.98px) {
    order: 1;
    flex: 0 0 40dvh;
    height: 40dvh;
    min-height: 40dvh;
    max-height: 40dvh;
    width: 100%;
    position: relative;
  }
`;

const ProfilePhoto = styled.div`
  background-size: cover;
  min-height: 700px;
  position: absolute;
  align-self: center;
  margin-left: 50%;
  width: 50%;
  filter: saturate(1);

  @media (max-width: 991.98px) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin-left: 0;
    min-height: unset;
    height: 100%;
    width: 100%;
  }

  @media (max-width: 420px) {
    background-position: 85% !important;
  }
`;

const ProfilePhoto1 = styled(ProfilePhoto)`
  background-image: url(${introdata.your_img_url});
  background-position: right;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  &:hover {
    opacity: 1;
  }
`;

const ProfilePhoto2 = styled(ProfilePhoto)`
  background-image: url(${introdata.your_img_url2});
  background-position: right;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  &:hover {
    opacity: 1;
  }
`;

const ProfilePhoto1Light = styled(ProfilePhoto)`
  background-image: url(${introdata.your_img_urlLight});
  background-position: center;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  &:hover {
    opacity: 0;
  }
`;

const ProfilePhoto2Light = styled(ProfilePhoto)`
  background-image: url(${introdata.your_img_url2Light});
  background-position: center;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  &:hover {
    opacity: 1;
  }
`;

const TextContainer = styled.div`
  width: 50%;

  h1 {
    font-size: 30px;
    font-weight: 700;

    @media (max-width: 420px) {
      font-size: 24px;
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 700;
  }

  @media (max-width: 991.98px) {
    width: 100%;
    margin-left: 0;
    order: 2;
    flex: 1;
    display: flex;
    align-items: flex-start;
    padding-top: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const Intro = styled.div`
  max-width: 450px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 991.98px) {
    max-width: 100%;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: calc(130px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
    width: 100%;
  }

  @media (max-width: 991px) {
    background: linear-gradient(
      to bottom,
      var(--primary-color),
      var(--primary-color-transparent)
    );
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  @media (max-width: 420px) {
    padding-left: 15px;
    padding-right: 15px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 500;
  padding-top: 100px;

  @media (max-width: 991px) {
    padding-top: 10px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;

  @media (max-width: 445px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionButton = styled.div`
  display: inline-block;
  padding: 4px 19px;
  color: var(--secondary-color);
  position: relative;
  border: var(--secondary-color) 2px solid;
  overflow: hidden;
  transition: all 0.6s cubic-bezier(0.55, 0, 0.1, 1);
  cursor: pointer;
  border-radius: 0;
  margin-right: 20px;
  margin-bottom: 10px;

  a {
    text-decoration: none;
  }

  &:hover {
    box-shadow: 8px 8px 0px var(--text-color), -8px -8px 0px var(--text-color);
  }

  &:hover .one {
    opacity: 1;
    transform: translate3d(0px, 0px, 0px);
  }

  &:hover .two {
    transform: translate3d(0px, 0px, 0px);
  }

  &:hover .three {
    transform: translate3d(0px, 0px, 0px);
  }

  @media (max-width: 445px) {
    margin-right: 0;
    width: 100%;
    text-align: center;
  }
`;

const Ring = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: transparent;
  top: 0;
  left: 0;
  transform: translate3d(0px, 90px, 0px);
`;

const RingOne = styled(Ring)`
  background-color: var(--text-color-3);
  transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
  z-index: -4;
`;

const RingTwo = styled(Ring)`
  background-color: var(--primary-color);
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
  z-index: -3;
`;

const RingThree = styled(Ring)`
  background-color: var(--secondary-color);
  transition: all 0.7s cubic-bezier(0.55, 0, 0.1, 1);
  z-index: -3;
`;

const PortfolioButton = styled(ActionButton)`
  background: var(--secondary-color);
  color: var(--primary-color);
`;

const ContactButton = styled(ActionButton)`
  &:hover {
    color: var(--primary-color);
  }
`;

const ArcadeButton = styled(ActionButton)`
  background: #000;
  color: #0f0;
  font-family: 'Press Start 2P', cursive;
  text-shadow: 0 0 5px #0f0;
  border: 2px solid #0f0;
  animation: ${flicker} 2s infinite;

  &:hover {
    box-shadow: 0 0 20px #0f0;
    color: #fff;
    text-shadow: 0 0 10px #0f0;
  }
`;

const ResumeButton = styled(ActionButton)``;

export const Home = () => {
  const [theme, settheme] = useState(localStorage.getItem("theme") || "dark");

  useEffect(() => {
    const handleThemeChange = (e) => {
      settheme(e.detail);
    };
    window.addEventListener("themeChanged", handleThemeChange);
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
    };
  }, []);

  return (
    <HelmetProvider>
      <HomeSection id="home">
        <Helmet>
          <meta charSet="utf-8" />
          <title> {meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        <IntroSection className="d-block d-lg-flex align-items-center">
          {theme === "dark" && (
            <PhotoContainer className="d-block d-lg-flex align-items-center">
              <ProfilePhoto1 className="order-1 order-lg-2 h-100" />
              <ProfilePhoto2 className="order-1 order-lg-2 h-100" />
            </PhotoContainer>
          )}
          {theme === "light" && (
            <PhotoContainer className="d-block d-lg-flex align-items-center">
              <ProfilePhoto1Light className="order-1 order-lg-2 h-100" />
              <ProfilePhoto2Light className="order-1 order-lg-2 h-100" />
            </PhotoContainer>
          )}
          <TextContainer className="order-2 order-lg-1 d-lg-flex justify-content-center">
            <div className="align-self-center">
              <Intro>
                <Title>{introdata.title}</Title>
                <h1 className="fluidz-48 mb-1x">
                  <Typewriter
                    options={{
                      strings: [
                        introdata.animated.subfirst,
                        introdata.animated.first,
                        introdata.animated.second,
                        introdata.animated.third,
                        introdata.animated.fourth,
                        introdata.animated.fifth,
                        introdata.animated.sixth,
                        introdata.animated.seventh,
                        introdata.animated.eighth,
                      ],
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 10,
                    }}
                  />
                </h1>
                <p className="mb-1x">{introdata.description}</p>
                <ButtonContainer className="pb-5">
                  <Link to="/portfolio" className="text_2">
                    <PortfolioButton>
                      My Portfolio
                      <RingOne className="one" />
                      <RingTwo className="two" />
                      <RingThree className="three" />
                    </PortfolioButton>
                  </Link>
                  <Link to="/contact">
                    <ContactButton>
                      Contact Me
                      <RingOne className="one" />
                      <RingTwo className="two" />
                      <RingThree className="three" />
                    </ContactButton>
                  </Link>
                  <Link to="/about">
                    <ContactButton>
                      About
                      <RingOne className="one" />
                      <RingTwo className="two" />
                      <RingThree className="three" />
                    </ContactButton>
                  </Link>
                  <a href="https://sonnyc56.github.io/" target="_blank" rel="noopener noreferrer">
                    <ArcadeButton>
                      Arcade
                      <RingOne className="one" />
                      <RingTwo className="two" />
                      <RingThree className="three" />
                    </ArcadeButton>
                  </a>
                  <Link to="/resume">
                    <ResumeButton>
                      Resume
                      <RingOne className="one" />
                      <RingTwo className="two" />
                      <RingThree className="three" />
                    </ResumeButton>
                  </Link>
                </ButtonContainer>
              </Intro>
            </div>
          </TextContainer>
        </IntroSection>
      </HomeSection>
    </HelmetProvider>
  );
};
